/**
 * Sign in — the redesigned two-panel screen.
 *
 * Left: the brand hero on an indigo→blue gradient. Right: the sign-in form.
 * The API hands back an access token and a refresh token; `lib/api` stores both
 * and refreshes silently from then on. Every style here is scoped under `.lx2`
 * in styles/login.css, so none of it reaches the app it lets you into.
 */
import { useState } from 'react';
import api from '../lib/api.js';
import { clearOfflineChoice } from '../lib/storage.js';
import '../styles/login.css';

export default function Login({ onSignedIn }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async e => {
    e.preventDefault();
    if (busy || done) return;
    setError('');

    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Enter a valid email address.');
      return;
    }
    if (!password) { setError('Enter your password.'); return; }

    setBusy(true);
    try {
      await api.auth.login(email.trim(), password);
      clearOfflineChoice();
      setBusy(false);
      setDone(true);
      setTimeout(onSignedIn, 450);
    } catch (err) {
      setBusy(false);
      setError(err?.message || 'That email and password do not match.');
    }
  };

  return (
    <div className="lx2">
      {/* ---- left: brand hero ---- */}
      <aside className="lx2-hero" aria-hidden="true">
        <div className="lx2-brand">
          <b>XYRA</b>
          <span>Client Master</span>
        </div>

        <div className="lx2-pitch">
          <h1>AI-powered accounting software for UAE businesses</h1>
          <p>
            Seven products. One AI-powered platform. From accounting to a virtual
            CFO, FTA-compliant from day one.
          </p>
          <div className="lx2-stats">
            <div className="lx2-stat"><b>4,500+</b><span>UAE businesses</span></div>
            <div className="lx2-stat"><b>4.9 / 5</b><span>Customer rating</span></div>
          </div>
        </div>

        <div className="lx2-foot">For the Xyra sales and success team</div>
      </aside>

      {/* ---- right: sign-in form ---- */}
      <main className="lx2-panel">
        <form className="lx2-form" onSubmit={submit} noValidate>
          <h2>Sign in</h2>
          <p className="lx2-formsub">Use your Xyra work account.</p>

          <button
            type="button"
            className="lx2-sso"
            onClick={() => setError('Zoho sign-in isn’t set up yet — use your work email below.')}
          >
            <span className="lx2-ssologo">Z</span>
            Continue with Zoho
          </button>

          <div className="lx2-or"><span>or use email</span></div>

          <div className="lxfield">
            <input
              id="email"
              type="email"
              placeholder=" "
              autoComplete="username"
              value={email}
              onChange={e => { setEmail(e.target.value); setError(''); }}
            />
            <label htmlFor="email">Work email</label>
          </div>

          <div className="lxfield">
            <input
              id="password"
              type={show ? 'text' : 'password'}
              placeholder=" "
              autoComplete="current-password"
              value={password}
              onChange={e => { setPassword(e.target.value); setError(''); }}
            />
            <label htmlFor="password">Password</label>
            <button
              type="button"
              className="lx2-showpw"
              aria-pressed={show}
              onClick={() => setShow(v => !v)}
            >
              {show ? 'Hide' : 'Show'}
            </button>
          </div>

          <p className={`lx2-error${error ? ' show' : ''}`} role="alert">{error}</p>

          <button
            type="submit"
            className={`lx2-submit${busy ? ' is-busy' : ''}${done ? ' is-done' : ''}`}
            disabled={busy || done}
          >
            {done ? 'Signed in ✓' : busy ? 'Signing in…' : 'Sign in'}
          </button>

          <p className="lx2-help">
            No account yet? Ask your admin to invite you. Accounts are created by
            Xyra, not self-signup.
          </p>
        </form>
      </main>
    </div>
  );
}
